import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { ClipboardList, Plus, Trash2, ArrowLeft, Save, FileText, Mail, Pencil, Search, X, ChevronDown, Calculator, Copy, GripVertical, Type, Wrench, CircleDashed, Sigma, Eye, CheckCircle, Send, Loader2 } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import KatalogAutocomplete from '../components/Katalog/KatalogAutocomplete';
import { kvaApi } from '../api/kvaApi';
import { getAllKunden, getKundeById } from '../api/kundenApi';
import { textbausteinApi } from '../api/textbausteinApi';
import DokumentVorschau from '../components/Dokumente/DokumentVorschau';

function KvaErstellen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams(); // Optional ID for edit mode
  const isEditMode = !!editId;
  const [searchParams] = useSearchParams();
  const fromDuplicate = location.state?.duplicate;
  const preselectedKundeId = searchParams.get('kundeId') || (fromDuplicate?.kundeId || '');
  const preselectedAnfrageId = searchParams.get('anfrageId') || (fromDuplicate?.anfrageId || '');

  const [kunden, setKunden] = useState([]);
  const [kundenAnfragen, setKundenAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnfragen, setLoadingAnfragen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Formular-State
  const [kvaData, setKvaData] = useState(null); // Original KVA data for edit mode
  const [kvaNummer, setKvaNummer] = useState(''); // KVA number for edit mode display
  const [kundeId, setKundeId] = useState(preselectedKundeId || '');
  const [anfrageId, setAnfrageId] = useState(preselectedAnfrageId || '');
  const [gueltigkeitTage, setGueltigkeitTage] = useState(30);
  const [einleitung, setEinleitung] = useState('');
  const [schlusstext, setSchlusstext] = useState('');

  // Positionen
  const [positionen, setPositionen] = useState([
    { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }
  ]);

  // Dokumenttyp und Verbindlichkeit
  const [dokumentTyp, setDokumentTyp] = useState('KOSTENVORANSCHLAG');
  const [verbindlich, setVerbindlich] = useState(false);
  const [toleranzProzent, setToleranzProzent] = useState(20);

  const [betreff, setBetreff] = useState('');
  const [kundenReferenz, setKundenReferenz] = useState('');
  const [gesamtRabattProzent, setGesamtRabattProzent] = useState(0);
  const [textbausteine, setTextbausteine] = useState([]);
  const [zahlungsbedingungen, setZahlungsbedingungen] = useState('');
  const [zuschlagBetrag, setZuschlagBetrag] = useState('');
  const [zuschlagBeschreibung, setZuschlagBeschreibung] = useState('');
  const [showVorschau, setShowVorschau] = useState(false);
  const [showVorschauModal, setShowVorschauModal] = useState(false);

  // Direkt versenden Option
  const [direktVersenden, setDirektVersenden] = useState(false);

  // Auftragsbestätigung State
  const [creatingAb, setCreatingAb] = useState(false);
  const [sendingAb, setSendingAb] = useState(false);

  // Kundensuche State
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  useEffect(() => {
    loadKunden();
    if (isEditMode) {
      loadExistingKva();
    } else if (fromDuplicate) {
      // Duplication: pre-fill form with data from the duplicated KVA
      setKundeId(fromDuplicate.kundeId || '');
      setAnfrageId(fromDuplicate.anfrageId || '');
      if (fromDuplicate.einleitung) setEinleitung(fromDuplicate.einleitung);
      if (fromDuplicate.schlusstext) setSchlusstext(fromDuplicate.schlusstext);
      if (fromDuplicate.betreff) setBetreff(fromDuplicate.betreff);
      if (fromDuplicate.kundenReferenz) setKundenReferenz(fromDuplicate.kundenReferenz);
      if (fromDuplicate.gesamtRabattProzent) setGesamtRabattProzent(fromDuplicate.gesamtRabattProzent);
      if (fromDuplicate.zahlungsbedingungen) setZahlungsbedingungen(fromDuplicate.zahlungsbedingungen);
      if (fromDuplicate.zuschlagBetrag) setZuschlagBetrag(fromDuplicate.zuschlagBetrag / 100);
      if (fromDuplicate.zuschlagBeschreibung) setZuschlagBeschreibung(fromDuplicate.zuschlagBeschreibung);
      if (fromDuplicate.dokumentTyp) setDokumentTyp(fromDuplicate.dokumentTyp);
      if (fromDuplicate.verbindlich !== undefined) setVerbindlich(fromDuplicate.verbindlich);
      if (fromDuplicate.toleranzProzent !== null && fromDuplicate.toleranzProzent !== undefined) setToleranzProzent(fromDuplicate.toleranzProzent);
      if (fromDuplicate.positionen?.length > 0) {
        setPositionen(fromDuplicate.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis, // Already in Euro from handleDuplicate
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte || []
        })));
      }
    }
  }, [editId]);

  useEffect(() => {
    textbausteinApi.getAll().then(setTextbausteine).catch(console.error);
  }, []);

  const loadExistingKva = async () => {
    try {
      const kva = await kvaApi.getKva(editId);
      setKvaData(kva);
      setKvaNummer(kva.nummer);
      setKundeId(kva.kundeId);
      // Kunde direkt aus dem KVA übernehmen statt ihn in der getAllKunden-Liste
      // zu suchen (die z.B. Ansprechpartner ausblendet) – sonst bleibt selectedKunde
      // null und die Vorschau sendet kundeId=null → "Kunde auswählen..."-Platzhalter.
      if (kva.kunde) setSelectedKunde(kva.kunde);
      setAnfrageId(kva.anfrageId);
      setEinleitung(kva.einleitung || '');
      setSchlusstext(kva.schlusstext || '');

      // Calculate gueltigkeitTage from datum to gueltigBis
      if (kva.datum && kva.gueltigBis) {
        const datum = new Date(kva.datum);
        const gueltigBis = new Date(kva.gueltigBis);
        const diffTime = Math.abs(gueltigBis - datum);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setGueltigkeitTage(diffDays);
      }

      // Dokumenttyp und Verbindlichkeit laden
      if (kva.dokumentTyp) setDokumentTyp(kva.dokumentTyp);
      if (kva.verbindlich !== undefined) setVerbindlich(kva.verbindlich);
      if (kva.toleranzProzent !== null && kva.toleranzProzent !== undefined) setToleranzProzent(kva.toleranzProzent);

      if (kva.betreff) setBetreff(kva.betreff);
      if (kva.kundenReferenz) setKundenReferenz(kva.kundenReferenz);
      if (kva.gesamtRabattProzent) setGesamtRabattProzent(kva.gesamtRabattProzent);
      if (kva.zahlungsbedingungen) setZahlungsbedingungen(kva.zahlungsbedingungen);
      if (kva.zuschlagBetrag) setZuschlagBetrag(kva.zuschlagBetrag / 100); // Cent → Euro
      if (kva.zuschlagBeschreibung) setZuschlagBeschreibung(kva.zuschlagBeschreibung);

      // Load positionen - convert cent prices back to euros
      if (kva.positionen && kva.positionen.length > 0) {
        const loadedPositionen = kva.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Convert cents to euros
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        }));
        setPositionen(loadedPositionen);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Kostenvoranschlags:', error);
      alert('Kostenvoranschlag konnte nicht geladen werden');
      navigate('/kostenvoranschlaege');
    }
  };

  // Anfragen laden wenn Kunde ausgewählt wird
  useEffect(() => {
    if (kundeId) {
      loadKundenAnfragen(kundeId);
    } else {
      setKundenAnfragen([]);
      setAnfrageId('');
    }
  }, [kundeId]);

  // Aufmaße bei vorausgewählter Anfrage übernehmen (nach Laden der Anfragen)
  useEffect(() => {
    if (preselectedAnfrageId && kundenAnfragen.length > 0 && !isEditMode && !einleitung.trim()) {
      const selectedAnfrage = kundenAnfragen.find(a => a.id === preselectedAnfrageId);
      if (selectedAnfrage) {
        const aufmasse = selectedAnfrage.extrahierteDaten?.aufmasse || [];
        if (aufmasse.length > 0) {
          const aufmasseText = aufmasse.map(aufmass => {
            let text = `• ${aufmass.bezeichnung}: ${aufmass.breite} × ${aufmass.hoehe}`;
            if (aufmass.tiefe) text += ` × ${aufmass.tiefe}`;
            text += ` ${aufmass.einheit}`;
            if (aufmass.material) text += ` (${aufmass.material})`;
            if (aufmass.notizen) text += ` - ${aufmass.notizen}`;
            if (aufmass.customFields && aufmass.customFields.length > 0) {
              const customText = aufmass.customFields.map(f => `${f.name}: ${f.value}`).join(', ');
              text += ` [${customText}]`;
            }
            return text;
          }).join('\n');
          setEinleitung(`Erfasste Aufmaße:\n${aufmasseText}\n\n`);
        }
      }
    }
  }, [kundenAnfragen, preselectedAnfrageId, isEditMode]);

  // Click-Outside Handler für Kundensuche
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Setze selectedKunde wenn Kunden geladen und kundeId vorhanden
  useEffect(() => {
    if (kundeId && kunden.length > 0 && !selectedKunde) {
      // kundeId ist ein UUID-String, nicht parseInt verwenden!
      const kunde = kunden.find(k => k.id === kundeId);
      if (kunde) {
        setSelectedKunde(kunde);
      }
    }
  }, [kunden, kundeId]);

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);

      // Wenn Kunde vorausgewählt, lade dessen Anfragen
      if (preselectedKundeId) {
        await loadKundenAnfragen(preselectedKundeId);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKundenAnfragen = async (selectedKundeId) => {
    try {
      setLoadingAnfragen(true);
      const kundeData = await getKundeById(selectedKundeId);
      setKundenAnfragen(kundeData.anfragen || []);
    } catch (error) {
      console.error('Fehler beim Laden der Anfragen:', error);
      setKundenAnfragen([]);
    } finally {
      setLoadingAnfragen(false);
    }
  };

  const handleKundeChange = (newKundeId) => {
    setKundeId(newKundeId);
    setAnfrageId(''); // Anfrage zurücksetzen bei Kundenwechsel
  };

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  // Kunde auswählen
  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setKundeId(kunde.id);
    setKundenSuche('');
    setShowKundenDropdown(false);
  };

  // Kundenauswahl entfernen
  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setKundeId('');
    setKundenSuche('');
    setAnfrageId('');
  };

  // Anfrage auswählen und Aufmaße übernehmen
  const handleAnfrageChange = (newAnfrageId) => {
    setAnfrageId(newAnfrageId);

    // Wenn keine Anfrage ausgewählt oder im Edit-Modus, nichts tun
    if (!newAnfrageId || isEditMode) return;

    // Ausgewählte Anfrage finden
    const selectedAnfrage = kundenAnfragen.find(a => a.id === newAnfrageId);
    if (!selectedAnfrage) return;

    // Prüfen ob Aufmaße vorhanden sind
    const aufmasse = selectedAnfrage.extrahierteDaten?.aufmasse || [];
    if (aufmasse.length === 0) return;

    // Aufmaße formatieren für die Einleitung
    const aufmasseText = aufmasse.map(aufmass => {
      let text = `• ${aufmass.bezeichnung}: ${aufmass.breite} × ${aufmass.hoehe}`;
      if (aufmass.tiefe) text += ` × ${aufmass.tiefe}`;
      text += ` ${aufmass.einheit}`;
      if (aufmass.material) text += ` (${aufmass.material})`;
      if (aufmass.notizen) text += ` - ${aufmass.notizen}`;
      // Eigene Felder hinzufügen
      if (aufmass.customFields && aufmass.customFields.length > 0) {
        const customText = aufmass.customFields.map(f => `${f.name}: ${f.value}`).join(', ');
        text += ` [${customText}]`;
      }
      return text;
    }).join('\n');

    // In die Einleitung einfügen (nur wenn leer oder nach Rückfrage)
    const aufmasseHeader = `Erfasste Aufmaße:\n${aufmasseText}\n\n`;
    if (!einleitung.trim()) {
      setEinleitung(aufmasseHeader);
    } else if (!einleitung.includes('Erfasste Aufmaße:')) {
      // Vor bestehenden Text einfügen
      setEinleitung(aufmasseHeader + einleitung);
    }
  };

  const addPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }
    ]);
  };

  const removePosition = (index) => {
    if (positionen.length === 1) {
      alert('Mindestens eine Position erforderlich');
      return;
    }
    setPositionen(positionen.filter((_, i) => i !== index));
  };

  const duplicatePosition = (index) => {
    const pos = { ...positionen[index] };
    const updated = [...positionen];
    updated.splice(index + 1, 0, pos);
    setPositionen(updated);
  };

  const movePosition = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= positionen.length) return;
    const updated = [...positionen];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setPositionen(updated);
  };

  const addTextPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 0, einheit: '', einzelpreis: 0, ustSatz: 0, istLohn: false, positionsTyp: 'TEXT' }
    ]);
  };

  const addOptionalPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'OPTIONAL', rabattProzent: 0 }
    ]);
  };

  const addZwischensumme = () => {
    setPositionen([
      ...positionen,
      { beschreibung: 'Zwischensumme', menge: 0, einheit: '', einzelpreis: 0, ustSatz: 0, istLohn: false, positionsTyp: 'ZWISCHENSUMME' }
    ]);
  };

  const calculateZwischensumme = (index) => {
    let sum = 0;
    for (let i = index - 1; i >= 0; i--) {
      if (positionen[i].positionsTyp === 'ZWISCHENSUMME') break;
      if (positionen[i].positionsTyp === 'NORMAL') {
        sum += calculatePositionGesamt(positionen[i]);
      }
    }
    return sum;
  };

  const updatePosition = (index, field, value) => {
    const updated = [...positionen];
    updated[index][field] = value;
    setPositionen(updated);
  };

  // Berechnungsschritte helpers
  const toggleBerechnungsSchritte = (posIndex) => {
    const updated = [...positionen];
    const pos = updated[posIndex];
    if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
      // Turn off — clear steps
      pos.berechnungsSchritte = [];
    } else {
      // Turn on — add one empty step
      pos.berechnungsSchritte = [{ beschreibung: '', menge: 1, einheit: pos.einheit || 'Stk', einzelpreis: '', gesamt: 0 }];
    }
    setPositionen(updated);
  };

  const addBerechnungsSchritt = (posIndex) => {
    const updated = [...positionen];
    if (!updated[posIndex].berechnungsSchritte) updated[posIndex].berechnungsSchritte = [];
    updated[posIndex].berechnungsSchritte.push({ beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', gesamt: 0 });
    setPositionen(updated);
  };

  const removeBerechnungsSchritt = (posIndex, schrittIndex) => {
    const updated = [...positionen];
    updated[posIndex].berechnungsSchritte.splice(schrittIndex, 1);
    setPositionen(updated);
  };

  const updateBerechnungsSchritt = (posIndex, schrittIndex, field, value) => {
    const updated = [...positionen];
    const schritt = updated[posIndex].berechnungsSchritte[schrittIndex];
    schritt[field] = value;
    // Recalculate step gesamt
    const m = parseFloat(schritt.menge) || 0;
    const p = parseFloat(schritt.einzelpreis) || 0;
    schritt.gesamt = m * p;
    setPositionen(updated);
  };

  // Berechnung
  const calculatePositionGesamt = (pos) => {
    let gesamt;
    if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
      gesamt = pos.berechnungsSchritte.reduce((sum, s) => {
        const m = parseFloat(s.menge) || 0;
        const p = parseFloat(s.einzelpreis) || 0;
        return sum + m * p;
      }, 0);
    } else {
      const preis = parseFloat(pos.einzelpreis) || 0;
      const menge = parseFloat(pos.menge) || 0;
      gesamt = preis * menge;
    }
    const rabatt = parseFloat(pos.rabattProzent) || 0;
    if (rabatt > 0) {
      gesamt = gesamt * (1 - rabatt / 100);
    }
    return gesamt;
  };

  const calculateNetto = () => {
    const netto = positionen
      .filter(p => p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL' && p.positionsTyp !== 'ZWISCHENSUMME')
      .reduce((sum, pos) => sum + calculatePositionGesamt(pos), 0);
    const nachRabatt = netto * (1 - (parseFloat(gesamtRabattProzent) || 0) / 100);
    return nachRabatt + (parseFloat(zuschlagBetrag) || 0);
  };

  const calculateUst = () => {
    const rabattFaktor = 1 - (parseFloat(gesamtRabattProzent) || 0) / 100;
    const posUst = positionen
      .filter(p => p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL' && p.positionsTyp !== 'ZWISCHENSUMME')
      .reduce((sum, pos) => {
        const gesamt = calculatePositionGesamt(pos) * rabattFaktor;
        return sum + (gesamt * pos.ustSatz / 100);
      }, 0);
    const zuschlagUst = (parseFloat(zuschlagBetrag) || 0) * 0.19;
    return posUst + zuschlagUst;
  };

  const calculateBrutto = () => {
    return calculateNetto() + calculateUst();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // --- Auftragsbestätigung (AB) ---
  const handleCreateAB = async () => {
    const typText = dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA';
    if (!confirm(`Auftragsbestätigung für ${typText} ${kvaNummer} erstellen?`)) return;
    try {
      setCreatingAb(true);
      const result = await kvaApi.createAB(editId);
      setKvaData(result);
      alert(`Auftragsbestätigung ${result.abNummer} wurde erstellt.`);
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler bei der AB-Erstellung');
    } finally {
      setCreatingAb(false);
    }
  };

  const handleSendAB = async () => {
    if (!confirm(`Auftragsbestätigung ${kvaData?.abNummer} an ${selectedKunde?.email || 'Kunden'} senden?`)) return;
    try {
      setSendingAb(true);
      const result = await kvaApi.sendAB(editId);
      alert(result.message);
      // Reload to get updated data
      const updated = await kvaApi.getKva(editId);
      setKvaData(updated);
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim AB-Versand');
    } finally {
      setSendingAb(false);
    }
  };

  const handleConvertAbToRechnung = async () => {
    if (!confirm(`Auftragsbestätigung ${kvaData?.abNummer} in eine Rechnung umwandeln?`)) return;
    try {
      const conversionData = await kvaApi.convertAbToRechnung(editId);
      navigate('/rechnungen/neu', { state: { fromKva: conversionData } });
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler bei der Konvertierung');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validierung
    if (!kundeId) {
      alert('Bitte wählen Sie einen Kunden aus');
      return;
    }

    if (!anfrageId) {
      alert('Bitte wählen Sie eine Anfrage aus. Kostenvoranschläge müssen einer Anfrage zugeordnet sein.');
      return;
    }

    const gueltigePositionen = positionen.filter(
      p => p.beschreibung.trim() && (p.positionsTyp === 'TEXT' || p.positionsTyp === 'ZWISCHENSUMME' || parseFloat(p.einzelpreis) > 0)
    );

    if (gueltigePositionen.length === 0) {
      alert('Bitte fügen Sie mindestens eine gültige Position hinzu');
      return;
    }

    try {
      setSaving(true);

      // Positionen für API vorbereiten (Preise in Cent!)
      const positionenData = gueltigePositionen.map(pos => {
        if (pos.positionsTyp === 'ZWISCHENSUMME') {
          return {
            beschreibung: pos.beschreibung,
            menge: 0,
            einheit: '',
            einzelpreis: 0,
            ustSatz: 0,
            istLohn: false,
            positionsTyp: 'ZWISCHENSUMME',
            rabattProzent: 0
          };
        }
        const posData = {
          beschreibung: pos.beschreibung,
          menge: parseFloat(pos.menge) || 1,
          einheit: pos.einheit,
          einzelpreis: Math.round(parseFloat(pos.einzelpreis) * 100), // Euro zu Cent!
          ustSatz: parseFloat(pos.ustSatz),
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: parseFloat(pos.rabattProzent) || 0
        };
        if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
          posData.berechnungsSchritte = pos.berechnungsSchritte
            .filter(s => s.beschreibung.trim())
            .map(s => ({
              beschreibung: s.beschreibung,
              menge: parseFloat(s.menge) || 1,
              einheit: s.einheit || 'Stk',
              einzelpreis: Math.round(parseFloat(s.einzelpreis) * 100),
              gesamt: Math.round((parseFloat(s.menge) || 1) * (parseFloat(s.einzelpreis) || 0) * 100)
            }));
        }
        return posData;
      });

      let result;

      if (isEditMode) {
        // Update existing KVA
        const updateData = {
          einleitung: einleitung || null,
          schlusstext: schlusstext || null,
          positionen: positionenData,
          dokumentTyp,
          verbindlich,
          toleranzProzent: dokumentTyp === 'KOSTENVORANSCHLAG' && !verbindlich ? toleranzProzent : null,
          betreff: betreff || null,
          kundenReferenz: kundenReferenz || null,
          gesamtRabattProzent: parseFloat(gesamtRabattProzent) || 0,
          zahlungsbedingungen: zahlungsbedingungen || null,
          zuschlagBetrag: Math.round((parseFloat(zuschlagBetrag) || 0) * 100),
          zuschlagBeschreibung: zuschlagBeschreibung || null,
        };

        result = await kvaApi.updateKva(editId, updateData);

        const typText = dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'Kostenvoranschlag';

        // Falls Direktversand aktiviert, sende E-Mail
        if (direktVersenden) {
          try {
            const sendResult = await kvaApi.sendKva(result.id);
            alert(`${typText} ${result.nummer} wurde aktualisiert und an ${sendResult.kva.versendetAn} versendet!`);
          } catch (sendError) {
            console.error('Fehler beim Versenden:', sendError);
            alert(`${typText} ${result.nummer} wurde aktualisiert, aber konnte nicht versendet werden: ${sendError.response?.data?.error || sendError.message}`);
          }
        } else {
          alert(`${typText} ${result.nummer} wurde aktualisiert!`);
        }
      } else {
        // Create new KVA
        const data = {
          kundeId,
          anfrageId,
          gueltigkeitTage: parseInt(gueltigkeitTage),
          einleitung: einleitung || null,
          schlusstext: schlusstext || null,
          positionen: positionenData,
          dokumentTyp,
          verbindlich,
          toleranzProzent: dokumentTyp === 'KOSTENVORANSCHLAG' && !verbindlich ? toleranzProzent : null,
          betreff: betreff || null,
          kundenReferenz: kundenReferenz || null,
          gesamtRabattProzent: parseFloat(gesamtRabattProzent) || 0,
          zahlungsbedingungen: zahlungsbedingungen || null,
          zuschlagBetrag: Math.round((parseFloat(zuschlagBetrag) || 0) * 100), // Euro zu Cent
          zuschlagBeschreibung: zuschlagBeschreibung || null,
        };

        result = await kvaApi.createKva(data);

        const typTextCreate = dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'Kostenvoranschlag';

        // Falls Direktversand aktiviert, sende E-Mail
        if (direktVersenden) {
          try {
            const sendResult = await kvaApi.sendKva(result.id);
            alert(`${typTextCreate} ${result.nummer} wurde erstellt und an ${sendResult.kva.versendetAn} versendet!`);
          } catch (sendError) {
            console.error('Fehler beim Versenden:', sendError);
            alert(`${typTextCreate} ${result.nummer} wurde erstellt, aber konnte nicht versendet werden: ${sendError.response?.data?.error || sendError.message}`);
          }
        } else {
          alert(`${typTextCreate} ${result.nummer} wurde erstellt!`);
        }
      }

      navigate('/kostenvoranschlaege');

    } catch (error) {
      console.error(`Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'}:`, error);
      alert(`Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'}: ` + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/kostenvoranschlaege')}
            className="p-2 hover:bg-gray-100 rounded-button transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
              {isEditMode ? <Pencil className="w-8 h-8 text-primary" /> : <ClipboardList className="w-8 h-8 text-primary" />}
              {isEditMode
                ? `${dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'Kostenvoranschlag'} ${kvaNummer} bearbeiten`
                : `${dokumentTyp === 'ANGEBOT' ? 'Neues Angebot' : 'Neuer Kostenvoranschlag'}`}
            </h1>
            <p className="text-text-muted mt-1">
              {isEditMode
                ? `Bearbeiten Sie ${dokumentTyp === 'ANGEBOT' ? 'das Angebot' : 'den Kostenvoranschlag'}`
                : `Erstellen Sie ${dokumentTyp === 'ANGEBOT' ? 'ein neues Angebot' : 'einen neuen Kostenvoranschlag'}`}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-6">
              {/* Kunde und Anfrage auswählen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Kunde & Anfrage</h2>

                {/* Kunde auswählen */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-muted mb-2">
                    Kunde *
                  </label>

                  <div className="relative" ref={kundenDropdownRef}>
                    {/* Ausgewählter Kunde oder Suchfeld */}
                    {selectedKunde ? (
                      <div className="flex items-center justify-between w-full px-4 py-3 border border-border rounded-button bg-gray-50">
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-lg block truncate">{selectedKunde.name}</span>
                          {selectedKunde.email && (
                            <span className="text-text-muted text-sm block truncate">{selectedKunde.email}</span>
                          )}
                        </div>
                        {!isEditMode && (
                          <button
                            type="button"
                            onClick={handleKundeRemove}
                            className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={kundenSuche}
                          onChange={(e) => {
                            setKundenSuche(e.target.value);
                            setShowKundenDropdown(true);
                          }}
                          onFocus={() => setShowKundenDropdown(true)}
                          placeholder="Kunde suchen..."
                          className="w-full pl-10 pr-10 py-3 border border-border rounded-button focus:outline-none focus:ring-2 focus:ring-primary text-lg"
                          disabled={isEditMode}
                        />
                        <button
                          type="button"
                          onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          disabled={isEditMode}
                        >
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    )}

                    {/* Dropdown Liste */}
                    {showKundenDropdown && !selectedKunde && !isEditMode && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loading ? (
                          <div className="px-4 py-3 text-text-muted text-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto"></div>
                          </div>
                        ) : gefilterteKunden.length === 0 ? (
                          <div className="px-4 py-3 text-text-muted text-center">
                            {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                          </div>
                        ) : (
                          gefilterteKunden.map(kunde => (
                            <button
                              key={kunde.id}
                              type="button"
                              onClick={() => handleKundeSelect(kunde)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                            >
                              <div className="font-medium">{kunde.name}</div>
                              <div className="text-sm text-text-muted flex gap-3">
                                {kunde.email && <span>{kunde.email}</span>}
                                {kunde.telefon && <span>{kunde.telefon}</span>}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {isEditMode && (
                    <p className="text-xs text-text-muted mt-1">
                      Kunde kann bei bestehenden KVAs nicht geändert werden
                    </p>
                  )}
                </div>

                {/* Anfrage-Auswahl (PFLICHT!) */}
                {kundeId && (
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Zugehörige Anfrage *
                    </label>
                    {loadingAnfragen ? (
                      <div className="text-sm text-text-muted py-2">Lade Anfragen...</div>
                    ) : kundenAnfragen.length === 0 ? (
                      <div className="text-sm text-red-600 py-2 italic">
                        Keine Anfragen für diesen Kunden vorhanden. Bitte zuerst eine Anfrage erstellen.
                      </div>
                    ) : (
                      <select
                        value={anfrageId}
                        onChange={(e) => handleAnfrageChange(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-button bg-surface disabled:bg-gray-100 disabled:text-text-muted"
                        required
                        disabled={isEditMode}
                      >
                        <option value="">Anfrage auswählen *</option>
                        {kundenAnfragen.map(anfrage => {
                          const aufmasseCount = anfrage.extrahierteDaten?.aufmasse?.length || 0;
                          return (
                            <option key={anfrage.id} value={String(anfrage.id)}>
                              {new Date(anfrage.datum).toLocaleDateString('de-DE')} - {anfrage.betreff}
                              {anfrage.kategorie && ` (${anfrage.kategorie})`}
                              {aufmasseCount > 0 && ` [${aufmasseCount} Aufmaß${aufmasseCount > 1 ? 'e' : ''}]`}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    <p className="text-xs text-text-muted mt-1">
                      {isEditMode ? 'Anfrage kann bei bestehenden KVAs nicht geändert werden' : 'Kostenvoranschläge müssen einer Anfrage zugeordnet werden'}
                    </p>
                  </div>
                )}
              </div>

              {/* Dokumentkopf & Details */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Dokumentkopf & Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {/* Linke Seite */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Betreff / Titel</label>
                      <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="z.B. Angebot für Fenstermontage Musterstraße 1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Ihre Zeichen / Auftragsnr.</label>
                      <input type="text" value={kundenReferenz} onChange={(e) => setKundenReferenz(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="Referenznummer des Kunden (optional)" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Dokumenttyp</label>
                      <select value={dokumentTyp} onChange={(e) => setDokumentTyp(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button">
                        <option value="KOSTENVORANSCHLAG">Kostenvoranschlag</option>
                        <option value="ANGEBOT">Angebot</option>
                      </select>
                    </div>
                  </div>
                  {/* Rechte Seite */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Gültigkeitsdauer</label>
                      <select value={gueltigkeitTage} onChange={(e) => setGueltigkeitTage(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button">
                        <option value={14}>14 Tage</option>
                        <option value={21}>21 Tage</option>
                        <option value={30}>30 Tage</option>
                        <option value={60}>60 Tage</option>
                        <option value={90}>90 Tage</option>
                      </select>
                    </div>
                    <div>
                      <label className="flex items-center gap-3 p-3 border border-border rounded-button hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={verbindlich} onChange={(e) => setVerbindlich(e.target.checked)} className="w-4 h-4 text-primary rounded border-border" />
                        <span className="font-medium text-sm">{dokumentTyp === 'KOSTENVORANSCHLAG' ? 'Verbindlich (garantiert)' : 'Verbindlich (Festpreis)'}</span>
                      </label>
                    </div>
                    {dokumentTyp === 'KOSTENVORANSCHLAG' && !verbindlich && (
                      <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">Toleranz (%)</label>
                        <input type="number" value={toleranzProzent} onChange={(e) => setToleranzProzent(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))} min="0" max="50" className="w-full px-3 py-2 border border-border rounded-button" />
                      </div>
                    )}
                  </div>
                </div>
                {/* Rechtlicher Hinweis */}
                <div className="mt-4 p-3 bg-gray-50 rounded-button">
                  <p className="text-xs text-text-muted">
                    {dokumentTyp === 'KOSTENVORANSCHLAG' ? (
                      verbindlich ? 'Verbindlicher KVA — Preise sind garantiert.' : `Unverbindlicher KVA — Toleranz ±${toleranzProzent}% (§ 650c BGB).`
                    ) : (
                      verbindlich ? 'Verbindliches Angebot — bei Annahme entsteht ein Vertrag.' : 'Freibleibendes Angebot — Preisänderungen vorbehalten.'
                    )}
                  </p>
                </div>
              </div>

              {/* Positionen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">Positionen</h2>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={addPosition} className="flex items-center gap-1 text-primary hover:underline text-sm">
                      <Plus className="w-4 h-4" /> Position
                    </button>
                    <button type="button" onClick={addOptionalPosition} className="flex items-center gap-1 text-amber-600 hover:underline text-sm">
                      <CircleDashed className="w-4 h-4" /> Optionale Pos.
                    </button>
                    <button type="button" onClick={addZwischensumme} className="flex items-center gap-1 text-slate-500 hover:underline text-sm">
                      <Sigma className="w-4 h-4" /> Zwischensumme
                    </button>
                    <button type="button" onClick={addTextPosition} className="flex items-center gap-1 text-blue-600 hover:underline text-sm">
                      <Type className="w-4 h-4" /> Textposition
                    </button>
                  </div>
                </div>

                {/* Katalog-Schnellauswahl */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-muted mb-2">
                    Schnellauswahl aus Katalog
                  </label>
                  <KatalogAutocomplete
                    onSelect={(katalogPosition) => {
                      setPositionen([
                        ...positionen,
                        {
                          beschreibung: katalogPosition.bezeichnung,
                          menge: 1,
                          einheit: katalogPosition.einheit,
                          einzelpreis: katalogPosition.verkaufspreis,
                          ustSatz: katalogPosition.mwstSatz,
                          istLohn: katalogPosition.istLohn || false,
                          positionsTyp: 'NORMAL'
                        }
                      ]);
                    }}
                  />
                </div>

                <div className="space-y-4">
                  {positionen.map((pos, index) => (
                    <div key={index}>
                    {pos.positionsTyp === 'TEXT' ? (
                      <div className="border border-dashed border-blue-300 rounded-button p-4 bg-blue-50/30">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                            <Type className="w-4 h-4" /> Textposition {index + 1}
                          </span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => movePosition(index, -1)} className="p-1 text-gray-400 hover:text-gray-600 rounded transition" title="Nach oben"><GripVertical className="w-4 h-4" /></button>
                            <button type="button" onClick={() => duplicatePosition(index)} className="p-1 text-blue-500 hover:bg-blue-50 rounded transition" title="Duplizieren"><Copy className="w-4 h-4" /></button>
                            <button type="button" onClick={() => removePosition(index)} className="p-1 text-danger hover:bg-red-50 rounded transition"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <textarea
                          value={pos.beschreibung}
                          onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-border rounded-button text-sm"
                          placeholder="Überschrift oder Hinweistext..."
                        />
                      </div>
                    ) : (
                    <div className={`border rounded-button p-4 ${pos.positionsTyp === 'OPTIONAL' ? 'border-dashed border-amber-300 bg-amber-50/30' : 'border-border'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <span className={`text-sm font-medium ${pos.positionsTyp === 'OPTIONAL' ? 'text-amber-600 flex items-center gap-1' : 'text-text-muted'}`}>
                          {pos.positionsTyp === 'OPTIONAL' && <CircleDashed className="w-4 h-4" />}
                          Position {index + 1}
                          {pos.positionsTyp === 'OPTIONAL' && <span className="italic">(Optional)</span>}
                        </span>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => movePosition(index, -1)} className="p-1 text-gray-400 hover:text-gray-600 rounded transition" title="Nach oben"><GripVertical className="w-4 h-4" /></button>
                          <button type="button" onClick={() => duplicatePosition(index)} className="p-1 text-blue-500 hover:bg-blue-50 rounded transition" title="Duplizieren"><Copy className="w-4 h-4" /></button>
                          <button type="button" onClick={() => removePosition(index)} className="p-1 text-danger hover:bg-red-50 rounded transition"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-2 sm:gap-3">
                        {/* Beschreibung */}
                        <div className="col-span-12">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            Beschreibung
                          </label>
                          <input
                            type="text"
                            value={pos.beschreibung}
                            onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            placeholder="z.B. Fensterscheibe Küche 80x60cm"
                          />
                        </div>

                        {/* Menge */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            Menge
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={pos.menge}
                            onChange={(e) => updatePosition(index, 'menge', e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                          />
                        </div>

                        {/* Einheit */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            Einheit
                          </label>
                          <select
                            value={pos.einheit}
                            onChange={(e) => updatePosition(index, 'einheit', e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                          >
                            <option value="Stk">Stk</option>
                            <option value="Std">Std</option>
                            <option value="m²">m²</option>
                            <option value="m">m</option>
                            <option value="lfm">lfm</option>
                            <option value="kg">kg</option>
                            <option value="Pauschale">Pauschale</option>
                          </select>
                        </div>

                        {/* Einzelpreis */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            Preis (€)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={pos.einzelpreis}
                            onChange={(e) => updatePosition(index, 'einzelpreis', e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            placeholder="0.00"
                          />
                        </div>

                        {/* UST-Satz */}
                        <div className="col-span-4 sm:col-span-2">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            MwSt.
                          </label>
                          <select
                            value={pos.ustSatz}
                            onChange={(e) => updatePosition(index, 'ustSatz', parseFloat(e.target.value))}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                          >
                            <option value={19}>19%</option>
                            <option value={7}>7%</option>
                            <option value={0}>0%</option>
                          </select>
                        </div>

                        {/* Rabatt % */}
                        <div className="col-span-4 sm:col-span-2">
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">
                            Rabatt %
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={pos.rabattProzent || ''}
                            onChange={(e) => updatePosition(index, 'rabattProzent', e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            placeholder="0"
                          />
                        </div>

                        {/* Ist Lohn */}
                        {pos.positionsTyp !== 'TEXT' && (
                          <div className="col-span-6 sm:col-span-3 flex items-end pb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pos.istLohn || false}
                                onChange={(e) => updatePosition(index, 'istLohn', e.target.checked)}
                                className="w-4 h-4 text-primary rounded border-border"
                              />
                              <span className="text-xs sm:text-sm text-text-muted flex items-center gap-1">
                                <Wrench className="w-3.5 h-3.5" /> Lohn
                              </span>
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Berechnungsschritte Toggle */}
                      <div className="mt-3 border-t border-border pt-3">
                        <button
                          type="button"
                          onClick={() => toggleBerechnungsSchritte(index)}
                          className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded transition ${
                            pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0
                              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              : 'text-text-muted hover:bg-gray-100'
                          }`}
                        >
                          <Calculator className="w-3.5 h-3.5" />
                          Berechnungsschritte
                          {pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0 && (
                            <span className="bg-blue-200 text-blue-800 rounded-full px-1.5 text-[10px]">
                              {pos.berechnungsSchritte.length}
                            </span>
                          )}
                        </button>

                        {/* Berechnungsschritte Sub-Items */}
                        {pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0 && (
                          <div className="mt-2 ml-4 space-y-2">
                            {pos.berechnungsSchritte.map((schritt, sIdx) => (
                              <div key={sIdx} className="flex items-start gap-2 bg-gray-50 rounded p-2">
                                <div className="flex-1 grid grid-cols-12 gap-1.5">
                                  <div className="col-span-12 sm:col-span-4">
                                    <input
                                      type="text"
                                      value={schritt.beschreibung}
                                      onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'beschreibung', e.target.value)}
                                      className="w-full px-2 py-1 border border-border rounded text-xs"
                                      placeholder="Beschreibung"
                                    />
                                  </div>
                                  <div className="col-span-3 sm:col-span-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      value={schritt.menge}
                                      onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'menge', e.target.value)}
                                      className="w-full px-2 py-1 border border-border rounded text-xs"
                                      placeholder="Menge"
                                    />
                                  </div>
                                  <div className="col-span-3 sm:col-span-2">
                                    <select
                                      value={schritt.einheit}
                                      onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'einheit', e.target.value)}
                                      className="w-full px-1 py-1 border border-border rounded text-xs"
                                    >
                                      <option value="Stk">Stk</option>
                                      <option value="Std">Std</option>
                                      <option value="m²">m²</option>
                                      <option value="m">m</option>
                                      <option value="lfm">lfm</option>
                                      <option value="kg">kg</option>
                                      <option value="Pauschale">Pausch.</option>
                                    </select>
                                  </div>
                                  <div className="col-span-3 sm:col-span-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={schritt.einzelpreis}
                                      onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'einzelpreis', e.target.value)}
                                      className="w-full px-2 py-1 border border-border rounded text-xs"
                                      placeholder="Preis"
                                    />
                                  </div>
                                  <div className="col-span-3 sm:col-span-2 flex items-center justify-end">
                                    <span className="text-xs font-medium text-text-muted">
                                      {formatCurrency((parseFloat(schritt.menge) || 0) * (parseFloat(schritt.einzelpreis) || 0))}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeBerechnungsSchritt(index, sIdx)}
                                  className="p-1 text-danger hover:bg-red-50 rounded transition shrink-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addBerechnungsSchritt(index)}
                              className="flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <Plus className="w-3 h-3" />
                              Schritt hinzufügen
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Positions-Summe */}
                      <div className="mt-3 text-right text-sm">
                        <span className="text-text-muted">Netto: </span>
                        <span className="font-medium">
                          {formatCurrency(calculatePositionGesamt(pos))}
                        </span>
                      </div>
                    </div>
                    )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Texte */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Texte (optional)</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Einleitungstext
                    </label>
                    <textarea
                      value={einleitung}
                      onChange={(e) => setEinleitung(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="Text vor den Positionen... (Standard wird verwendet wenn leer)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Schlusstext
                    </label>
                    <textarea
                      value={schlusstext}
                      onChange={(e) => setSchlusstext(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="Text nach den Summen... (Standard wird verwendet wenn leer)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Zahlungsbedingungen
                    </label>
                    <textarea
                      value={zahlungsbedingungen}
                      onChange={(e) => setZahlungsbedingungen(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="z.B. Zahlbar innerhalb 14 Tagen ohne Abzug"
                    />
                  </div>

                  {textbausteine.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Textbaustein einfügen</label>
                      <select
                        onChange={(e) => {
                          const tb = textbausteine.find(t => t.id === e.target.value);
                          if (tb) {
                            if (tb.kategorie === 'EINLEITUNG') setEinleitung(prev => prev ? prev + '\n' + tb.inhalt : tb.inhalt);
                            else setSchlusstext(prev => prev ? prev + '\n' + tb.inhalt : tb.inhalt);
                          }
                          e.target.value = '';
                        }}
                        className="w-full px-3 py-2 border border-border rounded-button"
                      >
                        <option value="">Textbaustein auswählen...</option>
                        {textbausteine.map(tb => (
                          <option key={tb.id} value={tb.id}>[{tb.kategorie}] {tb.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Summen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  {/* Eingaben */}
                  <div className="grid grid-cols-3 gap-3 flex-1">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Zuschlag (€)</label>
                      <input type="number" step="0.01" min="0" value={zuschlagBetrag} onChange={(e) => setZuschlagBetrag(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Beschreibung</label>
                      <input type="text" value={zuschlagBeschreibung} onChange={(e) => setZuschlagBeschreibung(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="z.B. Anfahrt" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Gesamtrabatt (%)</label>
                      <input type="number" step="0.5" min="0" max="100" value={gesamtRabattProzent} onChange={(e) => setGesamtRabattProzent(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="0" />
                    </div>
                  </div>
                  {/* Berechnete Summen */}
                  <div className="sm:w-56 shrink-0 space-y-1">
                    {parseFloat(zuschlagBetrag) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-text-muted">{zuschlagBeschreibung || 'Zuschlag'}</span><span>+ {formatCurrency(parseFloat(zuschlagBetrag))}</span></div>
                    )}
                    <div className="flex justify-between text-sm"><span className="text-text-muted">Netto</span><span className="font-medium">{formatCurrency(calculateNetto())}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-text-muted">MwSt.</span><span>{formatCurrency(calculateUst())}</span></div>
                    <div className="flex justify-between text-base font-bold border-t border-border pt-1 mt-1"><span>Brutto</span><span className="text-primary">{formatCurrency(calculateBrutto())}</span></div>
                    {positionen.some(p => p.istLohn) && positionen.some(p => !p.istLohn && p.positionsTyp !== 'TEXT') && (
                      <div className="text-xs text-text-muted border-t border-border pt-1 mt-1 space-y-0.5">
                        <div className="flex justify-between"><span>Lohn</span><span>{formatCurrency(positionen.filter(p => p.istLohn && p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL').reduce((s, p) => s + calculatePositionGesamt(p), 0))}</span></div>
                        <div className="flex justify-between"><span>Material</span><span>{formatCurrency(positionen.filter(p => !p.istLohn && p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL').reduce((s, p) => s + calculatePositionGesamt(p), 0))}</span></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Spacer for sticky action bar */}
              <div className="h-24 sm:h-20"></div>
          </div>

          {/* Desktop Live-Vorschau (2xl+) */}
          <div className="hidden 2xl:block w-[550px] shrink-0">
            <DokumentVorschau
              data={{
                type: 'kva',
                kunde: selectedKunde,
                anfrageId,
                betreff,
                kundenReferenz,
                einleitung,
                schlusstext,
                zahlungsbedingungen,
                positionen,
                gesamtRabattProzent,
                zuschlagBetrag,
                zuschlagBeschreibung,
                dokumentTyp,
                verbindlich,
                toleranzProzent,
                gueltigkeitTage,
              }}
            />
          </div>
          </div>

          {/* Sticky Action Bar */}
          <div className="fixed inset-x-0 bottom-[80px] sm:bottom-0 z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
            <div className="container mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 sm:gap-3">
              {/* Vorschau Button (in der Bar, nicht floating) */}
              <button type="button" onClick={() => setShowVorschauModal(true)} className="2xl:hidden flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-100 transition shrink-0">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vorschau</span>
              </button>

              {/* AB erstellen Button (nur in Edit-Mode, wenn versendet/angenommen und noch keine AB) */}
              {isEditMode && kvaData && ['versendet', 'angenommen'].includes(kvaData.status) && !kvaData.abNummer && (
                <button type="button" onClick={handleCreateAB} disabled={creatingAb} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-button hover:bg-emerald-700 transition disabled:opacity-50 shrink-0">
                  {creatingAb ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  <span className="hidden sm:inline">AB erstellen</span>
                </button>
              )}

              {/* AB versenden (wenn AB erstellt, aber noch nicht versendet) */}
              {isEditMode && kvaData?.abNummer && !kvaData.abVersendetAm && (
                <button type="button" onClick={handleSendAB} disabled={sendingAb} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-button hover:bg-emerald-700 transition disabled:opacity-50 shrink-0">
                  {sendingAb ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="hidden sm:inline">AB senden</span>
                </button>
              )}

              {/* AB → Rechnung (wenn AB vorhanden) */}
              {isEditMode && kvaData?.abNummer && (
                <button type="button" onClick={handleConvertAbToRechnung} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-green-300 text-green-700 rounded-button hover:bg-green-50 transition shrink-0">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">AB → Rechnung</span>
                </button>
              )}

              {/* AB-Info Badge */}
              {isEditMode && kvaData?.abNummer && (
                <span className="text-xs text-emerald-600 font-medium hidden md:inline">
                  {kvaData.abNummer}
                  {kvaData.abVersendetAm && <span className="text-text-muted ml-1">(versendet)</span>}
                </span>
              )}

              {/* Mail Toggle */}
              {kundeId && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-button border transition shrink-0 ${direktVersenden ? 'bg-blue-50 border-blue-200' : 'border-border'}`}>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={direktVersenden} onChange={(e) => setDirektVersenden(e.target.checked)} className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <Mail className={`w-4 h-4 ${direktVersenden ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium hidden md:inline">{direktVersenden ? 'Versenden' : 'Nicht senden'}</span>
                </div>
              )}
              <div className="flex-1" />
              {!anfrageId && kundeId && !isEditMode && (
                <span className="text-xs text-red-500 hidden sm:inline">Anfrage fehlt</span>
              )}
              <button type="button" onClick={() => navigate('/kostenvoranschlaege')} className="px-3 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-button transition hidden sm:block">Abbrechen</button>
              <button type="submit" disabled={saving || (!isEditMode && !anfrageId)} className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 text-sm sm:text-base">
                {saving ? (
                  <span>{isEditMode ? 'Speichern...' : 'Erstellen...'}</span>
                ) : (
                  <>
                    {direktVersenden ? <Mail className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    <span className="hidden sm:inline">{direktVersenden ? (isEditMode ? `${dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA'} senden` : `${dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA'} erstellen & senden`) : (isEditMode ? 'Aktualisieren' : 'Erstellen')}</span>
                    <span className="sm:hidden">{direktVersenden ? 'Senden' : (isEditMode ? 'Speichern' : 'Erstellen')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Vorschau Modal (Mobile/Tablet) */}
          {showVorschauModal && (
            <DokumentVorschau
              isModal
              onClose={() => setShowVorschauModal(false)}
              data={{
                type: 'kva',
                kunde: selectedKunde,
                anfrageId,
                betreff,
                kundenReferenz,
                einleitung,
                schlusstext,
                zahlungsbedingungen,
                positionen,
                gesamtRabattProzent,
                zuschlagBetrag,
                zuschlagBeschreibung,
                dokumentTyp,
                verbindlich,
                toleranzProzent,
                gueltigkeitTage,
              }}
            />
          )}
        </form>
      </div>
    </Layout>
  );
}

export default KvaErstellen;
